export const CIVIL_CASE_TAXONOMY_CONFIG = {
  id: 'civil',
  label: 'Civil',
  description: 'Civil suits, appeals, revisions, executions, and miscellaneous civil proceedings.',
  active: true,
  types: [
    {
      id: 'Civil Suit',
      label: 'Civil Suit',
      shortLabel: 'Suit',
      description: 'Original civil proceedings such as declaration, injunction, recovery, possession, partition, specific performance, and damages.',
      legalBasis: 'Section 9 CPC (civil nature suits), Section 6 CPC (pecuniary jurisdiction), Sections 15 to 20 CPC (territorial jurisdiction).',
      courtOptions: [
        {
          id: 'civil_judge',
          label: 'Senior Civil Judge',
          description: 'Suit Value Below 5 Cror KP',
          recommendedIf: (value: number) => value < 50000000
        },
        {
          id: 'district_judge',
          label: 'District Judge',
          description: 'Suit Value Above 5 Cror KP',
          recommendedIf: (value: number) => value >= 50000000
        }
      ],
      sideOptions: ['Plaintiff', 'Defendant'],
      documentTabs: ['All', 'Pleadings', 'Applications', 'Evidence', 'Orders & Judgments', 'Miscellaneous'],
      defaultStages: [
        'Case Preparation', 'Filing / Institution', 'Summons / Notices', 
        'Defendant Appearance / Written Statement', 'Interim Applications', 
        'Framing of Issues', 'Plaintiff Evidence', 'Defendant Evidence', 
        'Rebuttal Evidence', 'Final Arguments', 'Judgment / Decree', 
        'Execution / Appeal', 'Closed'
      ],
      importantNotes: 'Recommended forum based on section 6 CPC pecuniary jurisdiction. Please verify according to local court practice.',
      aiContextHints: 'AI should understand plaint, written statement, replication, issues, evidence, decree, and execution linkage.',
      subtypes: [
        'Declaration',
        'Permanent Injunction',
        'Mandatory Injunction',
        'Specific Performance',
        'Recovery',
        'Damages',
        'Possession',
        'Partition',
        'Cancellation of Document',
        'Rendition of Accounts',
        'Defamation',
        'Mesne Profits',
        'Other'
      ]
    },
    {
      id: 'Civil Appeal',
      label: 'Civil Appeal',
      shortLabel: 'Appeal',
      description: 'Appeals against civil decrees, orders, or judgments.',
      legalBasis: 'Section 96 CPC (appeal from original decree), Section 104 and Order XLIII Rule 1 CPC (appeals from orders), Section 100 CPC (second appeal).',
      courtOptions: [
        {
          id: 'district_judge',
          label: 'Court of District Judge',
          description: 'Common forum for appeals from decrees/orders of Civil Judge/Senior Civil Judge.',
        },
        {
          id: 'high_court',
          label: 'High Court',
          description: 'For appeals that lie to the High Court, including first appeals, second appeals, or appeals from District Judge.',
        },
        {
          id: 'other_forum',
          label: 'Other / Custom Forum',
          description: 'Use if the appeal lies before a special forum or as advised by counsel.',
        }
      ],
      sideOptions: ['Appellant', 'Respondent'],
      documentTabs: ['All', 'Memo of Appeal', 'Applications', 'Impugned Order / Decree', 'Record', 'Orders & Judgments', 'Miscellaneous'],
      defaultStages: [
        'Case Preparation', 'Filing', 'Admission / Preliminary Hearing', 
        'Notice to Respondent', 'Respondent Appearance', 'Record Summoned / Received', 
        'Arguments', 'Judgment', 'Decree / Final Order', 'Further Appeal / Revision', 
        'Closed'
      ],
      importantNotes: '',
      aiContextHints: 'AI should understand that appeal drafting focuses on impugned judgment/order, grounds of appeal, limitation, certified copies, and prayer for setting aside/modification.',
      subtypes: [
        'Appeal Against Decree',
        'Appeal Against Order',
        'Regular First Appeal',
        'Regular Second Appeal',
        'Miscellaneous Appeal',
        'Other Civil Appeal'
      ]
    },
    {
      id: 'Civil Revision',
      label: 'Civil Revision',
      shortLabel: 'Revision',
      description: 'Revision against civil orders or decrees where jurisdictional error or material irregularity is alleged.',
      legalBasis: 'Section 115 CPC (revision).',
      courtOptions: [
        {
          id: 'high_court',
          label: 'High Court',
          description: 'Common forum for civil revisions under section 115 CPC.',
        },
        {
          id: 'district_judge',
          label: 'District Judge / Revisional Court',
          description: 'Use only where local law/practice provides revisional jurisdiction before District Judge or other forum.',
        },
        {
          id: 'other_forum',
          label: 'Other / Custom Forum',
          description: 'Use if required by special law or local practice.',
        }
      ],
      sideOptions: ['Petitioner', 'Respondent'],
      documentTabs: ['All', 'Revision Petition', 'Applications', 'Impugned Order / Record', 'Orders & Judgments', 'Miscellaneous'],
      defaultStages: [
        'Case Preparation', 'Filing', 'Preliminary Hearing / Admission', 
        'Notice to Respondent', 'Record Summoned', 'Arguments', 'Final Order', 
        'Further Remedy', 'Closed'
      ],
      importantNotes: '',
      aiContextHints: 'AI should focus on jurisdictional error, illegality, material irregularity, failure to exercise jurisdiction, and limits of revisional jurisdiction.',
      subtypes: [
        'Civil Revision Against Order',
        'Civil Revision Against Decree',
        'Jurisdictional Error',
        'Material Irregularity',
        'Illegal Exercise of Jurisdiction',
        'Failure to Exercise Jurisdiction',
        'Other Civil Revision'
      ]
    },
    {
      id: 'Execution Petition',
      label: 'Execution Petition',
      shortLabel: 'Execution',
      description: 'Proceedings for enforcement of a civil decree or order.',
      legalBasis: 'Section 36, 38, 39 CPC; Order XXI CPC (execution of decrees and orders).',
      courtOptions: [
        {
          id: 'executing_court',
          label: 'Executing Court / Court which passed the decree',
          description: 'Generally, the decree may be executed by the court which passed it.',
        },
        {
          id: 'transferee_court',
          label: 'Transferee Executing Court',
          description: 'Where the decree is transferred for execution under CPC.',
        },
        {
          id: 'district_judge_civil',
          label: 'District Judge / Civil Judge, as applicable',
          description: 'Use according to the court that passed the decree, transfer order, and local execution practice.',
        },
        {
          id: 'other_forum',
          label: 'Other / Custom Forum',
          description: 'Use where execution relates to a special decree/order or special forum.',
        }
      ],
      sideOptions: ['Decree Holder', 'Judgment Debtor'],
      documentTabs: ['All', 'Execution Petition', 'Applications', 'Decree / Order', 'Process & Warrants', 'Orders', 'Miscellaneous'],
      defaultStages: [
        'Case Preparation', 'Filing', 'Notice to Judgment Debtor', 
        'Objections by Judgment Debtor', 'Reply to Objections', 'Attachment / Process / Warrants', 
        'Evidence / Inquiry', 'Execution Proceedings', 'Satisfaction / Partial Satisfaction', 
        'Disposal / Consignment', 'Closed'
      ],
      importantNotes: '',
      aiContextHints: 'AI should understand decree holder, judgment debtor, executable decree/order, mode of execution, attachment, possession, calculation, satisfaction, and objections.',
      subtypes: [
        'Execution of Decree',
        'Execution of Order',
        'Execution of Compromise Decree',
        'Execution of Money Decree',
        'Execution of Possession Decree',
        'Execution of Injunction Decree',
        'Execution of Specific Performance Decree',
        'Other Execution'
      ]
    },
    {
      id: 'Miscellaneous Civil Application',
      label: 'Miscellaneous Civil Application',
      shortLabel: 'Application',
      description: 'Standalone or connected civil applications such as injunction, amendment, restoration, transfer, or summoning of record.',
      legalBasis: 'Varies by subtype (e.g. Order 39 R 1&2 CPC for injunction, Order 7 R 11 CPC for rejection of plaint).',
      courtOptions: [
        {
          id: 'same_court',
          label: 'Same Court Where Main Civil Case Is Pending',
          description: 'For applications connected with a pending suit, appeal, revision, or execution.',
        },
        {
          id: 'district_judge',
          label: 'District Judge',
          description: 'For transfer applications or matters where District Judge has jurisdiction.',
        },
        {
          id: 'high_court',
          label: 'High Court',
          description: 'For transfer/revision/constitutional or supervisory context where applicable.',
        },
        {
          id: 'other_forum',
          label: 'Other / Custom Forum',
          description: 'Use if the application belongs before another forum.',
        }
      ],
      sideOptions: ['Applicant', 'Respondent'],
      documentTabs: ['All', 'Application', 'Reply / Objections', 'Supporting Documents', 'Orders', 'Miscellaneous'],
      defaultStages: [
        'Case Preparation', 'Filing', 'Notice to Respondent', 'Reply / Objections', 
        'Rejoinder', 'Arguments', 'Order', 'Compliance / Further Proceedings', 'Closed'
      ],
      importantNotes: '',
      aiContextHints: 'AI should understand application-specific legal basis, prayer, affidavit, urgency, interim relief, reply, objections, and order.',
      subtypes: [
        'Temporary Injunction',
        'Rejection of Plaint',
        'Amendment of Pleadings',
        'Restoration',
        'Transfer Application',
        'Local Commission',
        'Summoning of Record',
        'Additional Evidence',
        'Condonation of Delay',
        'Setting Aside Ex Parte Proceedings',
        'Setting Aside Ex Parte Decree',
        'Exemption',
        'Urgent Hearing',
        'Other Application'
      ]
    },
    {
      id: 'Other Civil Matter',
      label: 'Other Civil Matter',
      shortLabel: 'Other Civil',
      description: 'Use this where the civil matter does not fit the available options.',
      legalBasis: '',
      courtOptions: [
        { id: 'civil_judge', label: 'Senior Civil Judge', description: 'Suit Value Below 5 Cror KP' },
        { id: 'district_judge', label: 'District Judge', description: 'Suit Value Above 5 Cror KP' },
        { id: 'high_court', label: 'High Court', description: '' },
        { id: 'special_forum', label: 'Special Civil Forum', description: '' },
        { id: 'other_forum', label: 'Other / Custom Court', description: '' }
      ],
      sideOptions: ['Party A', 'Party B'],
      documentTabs: ['All', 'Documents', 'Applications', 'Orders', 'Miscellaneous'],
      defaultStages: [
        'Case Preparation', 'Filing / Institution', 'Notices', 'Reply / Response', 'Proceedings',
        'Arguments / Hearing', 'Order / Judgment', 'Compliance / Further Remedy', 'Closed'
      ],
      importantNotes: '',
      aiContextHints: 'AI should rely on user-provided case description and documents.',
      subtypes: [
        'Custom Civil Matter',
        'Other'
      ]
    }
  ]
};

export const getCivilCaseTypes = () => CIVIL_CASE_TAXONOMY_CONFIG.types;

export const getCivilSubtypes = (caseTypeId: string) => {
  const type = CIVIL_CASE_TAXONOMY_CONFIG.types.find(t => t.id === caseTypeId);
  return type ? type.subtypes : [];
};

export const getSubtypeLegalBasis = (caseTypeId: string, subtypeId: string) => {
  if (caseTypeId === 'Miscellaneous Civil Application') {
    const basisMap: Record<string, string> = {
      'Temporary Injunction': 'Order XXXIX Rules 1 & 2 CPC',
      'Rejection of Plaint': 'Order VII Rule 11 CPC',
      'Amendment of Pleadings': 'Order VI Rule 17 CPC',
      'Restoration': 'Order IX CPC, where applicable',
      'Setting Aside Ex Parte Proceedings': 'Order IX CPC, where applicable',
      'Setting Aside Ex Parte Decree': 'Order IX CPC, where applicable',
      'Local Commission': 'Order XXVI CPC',
      'Additional Evidence': 'Order XLI Rule 27 CPC, where appellate context applies',
      'Condonation of Delay': 'Section 5 Limitation Act, where applicable',
      'Transfer Application': 'Section 24 CPC, where applicable'
    };
    return basisMap[subtypeId] || null;
  }
  return null;
};

import { normalizeCriminalCaseClassification } from './criminalTaxonomy';
import { normalizeFamilyCaseClassification } from './familyTaxonomy';
import { normalizeWritCaseClassification } from './writTaxonomy';
import { normalizeRentCaseClassification } from './rentTaxonomy';
import { normalizeRevenueLandCaseClassification } from './revenueLandTaxonomy';
import { normalizeBankingCaseClassification } from './bankingTaxonomy';
import { normalizeConsumerCaseClassification } from './consumerTaxonomy';
import { normalizeNabCaseClassification } from './nabTaxonomy';
import { normalizeFiaCaseClassification } from './fiaTaxonomy';

export const normalizeCivilCaseClassification = (existingCase: any) => {
  let normalizedCase = { ...existingCase };

  // Relief compatibility layer
  if (!normalizedCase.reliefsClaimed || !Array.isArray(normalizedCase.reliefsClaimed)) {
    if (normalizedCase.primaryRelief) {
      normalizedCase.reliefsClaimed = [normalizedCase.primaryRelief];
      normalizedCase.additionalReliefs = [];
    } else if (normalizedCase.caseSubtype && normalizedCase.caseSubtype !== 'Other') {
      normalizedCase.primaryRelief = normalizedCase.caseSubtype;
      normalizedCase.reliefsClaimed = [normalizedCase.caseSubtype];
      normalizedCase.additionalReliefs = [];
    } else {
      normalizedCase.reliefsClaimed = [];
      normalizedCase.additionalReliefs = [];
    }
  }

  if (normalizedCase.caseCategory) {
    return normalizedCase; // Already has category
  }

  // First try criminal normalization mapping
  const criminalNorm = normalizeCriminalCaseClassification(normalizedCase);
  if (criminalNorm.caseCategory === 'Criminal') {
    return criminalNorm;
  }

  // Next try family normalization mapping
  const familyNorm = normalizeFamilyCaseClassification(normalizedCase);
  if (familyNorm.caseCategory === 'Family') {
    return familyNorm;
  }

  // Next try writ normalization mapping
  const writNorm = normalizeWritCaseClassification(normalizedCase);
  if (writNorm.caseCategory === 'Constitutional / Writ') {
    return writNorm;
  }

  // Next try rent normalization mapping
  const rentNorm = normalizeRentCaseClassification(normalizedCase);
  if (rentNorm.caseCategory === 'Rent / Tenancy') {
    return rentNorm;
  }
  
  // Next try revenue/land normalization mapping
  const revenueLandNorm = normalizeRevenueLandCaseClassification(normalizedCase);
  if (revenueLandNorm.caseCategory === 'Revenue / Land') {
    return revenueLandNorm;
  }
  
  // Next try banking normalization mapping
  const bankingNorm = normalizeBankingCaseClassification(normalizedCase);
  if (bankingNorm.caseCategory === 'Banking / Financial Recovery') {
    return bankingNorm;
  }
  
  // Next try consumer normalization mapping
  const consumerNorm = normalizeConsumerCaseClassification(normalizedCase);
  if (consumerNorm.caseCategory === 'Consumer Protection') {
    return consumerNorm;
  }
  
  // Next try NAB
  const nabNorm = normalizeNabCaseClassification(normalizedCase);
  if (nabNorm.caseCategory === 'NAB / Accountability') {
    return nabNorm;
  }

  // Next try FIA
  const fiaNorm = normalizeFiaCaseClassification(normalizedCase);
  if (fiaNorm.caseCategory === 'FIA / Cybercrime / Federal Offences') {
    return fiaNorm;
  }

  const legacyType = normalizedCase.matterType || normalizedCase.caseTypeName || normalizedCase.caseType || '';
  const lowerType = String(legacyType).toLowerCase();

  let caseCategory = undefined;
  let caseType = legacyType;
  let caseSubtype = undefined;

  // Civil Suit mappings
  if (lowerType === 'civil suit' || lowerType.includes('suit for')) {
    caseCategory = 'Civil';
    caseType = 'Civil Suit';
    
    if (lowerType.includes('declaration')) caseSubtype = 'Declaration';
    else if (lowerType.includes('injunction')) caseSubtype = 'Permanent Injunction';
    else if (lowerType.includes('recovery')) caseSubtype = 'Recovery';
    else if (lowerType.includes('possession')) caseSubtype = 'Possession';
    else if (lowerType.includes('partition')) caseSubtype = 'Partition';
    else if (lowerType.includes('specific performance')) caseSubtype = 'Specific Performance';
  } 
  // Appeals
  else if (lowerType === 'civil appeal' || (lowerType.includes('appeal against') && !lowerType.includes('conviction') && !lowerType.includes('acquittal') && !lowerType.includes('sentence'))) {
    caseCategory = 'Civil';
    caseType = 'Civil Appeal';
    
    if (lowerType.includes('decree')) caseSubtype = 'Appeal Against Decree';
    else if (lowerType.includes('order')) caseSubtype = 'Appeal Against Order';
  }
  // Revision
  else if (lowerType === 'civil revision') {
    caseCategory = 'Civil';
    caseType = 'Civil Revision';
  }
  // Execution
  else if (lowerType === 'execution petition' || lowerType.includes('execution')) {
    caseCategory = 'Civil';
    caseType = 'Execution Petition';
  }
  // Apps
  else if (lowerType === 'civil miscellaneous application' || (lowerType.includes('application') && !lowerType.includes('superdari') && !lowerType.includes('appearance'))) {
    caseCategory = 'Civil';
    caseType = 'Miscellaneous Civil Application';
  } else if (CIVIL_CASE_TAXONOMY_CONFIG.types.some(t => t.id.toLowerCase() === lowerType)) {
      caseCategory = 'Civil';
      caseType = CIVIL_CASE_TAXONOMY_CONFIG.types.find(t => t.id.toLowerCase() === lowerType)?.id || caseType;
  }

  return {
    ...normalizedCase,
    ...(caseCategory && { caseCategory }),
    caseType,
    ...(caseSubtype && { caseSubtype }),
    legacyCaseType: legacyType
  };
};

export const getCaseClassificationLabel = (caseData: any) => {
  const norm = normalizeCivilCaseClassification(caseData);
  let label = norm.caseType || 'Uncategorized';
  
  if (norm.caseCategory === 'Civil') {
    label = 'Civil';
    if (norm.caseType) {
      if (norm.caseType.includes('Appeal')) label += ' · Appeal';
      else if (norm.caseType.includes('Revision')) label += ' · Revision';
      else if (norm.caseType.includes('Execution')) label += ' · Execution';
      else if (norm.caseType.includes('Application')) label += ' · Application';
      else if (norm.caseType.includes('Suit')) label += ' · Suit';
      else label += ` · ${norm.caseType}`;
      
      if (norm.caseSubtype && norm.caseSubtype !== 'Other') {
        label += ` · ${norm.caseSubtype}`;
      } else if (norm.primaryRelief) {
        label += ` · ${norm.primaryRelief}`;
      }
    }
  } else if (norm.caseCategory === 'Criminal') {
    label = 'Criminal';
    if (norm.caseType) {
      if (norm.caseType.includes('Appeal')) label += ' · Appeal';
      else if (norm.caseType.includes('Revision')) label += ' · Revision';
      else if (norm.caseType.includes('Application')) label += ' · Application';
      else if (norm.caseType.includes('Trial')) label += ' · Trial';
      else if (norm.caseType.includes('Bail') && norm.caseType !== 'Protective Bail') label += ' · Bail';
      else if (norm.caseType.includes('Complaint')) label += ' · Complaint';
      else if (norm.caseType.includes('Quashment')) label += ' · Quashment';
      else label += ` · ${norm.caseType}`;
      
      if (norm.caseSubtype && norm.caseSubtype !== 'Other') {
        label += ` · ${norm.caseSubtype}`;
      } else if (norm.primaryRelief) {
        label += ` · ${norm.primaryRelief}`;
      }
    }
  } else if (norm.caseCategory === 'Family') {
    label = 'Family';
    if (norm.caseType) {
      if (norm.caseType === 'Family Suit') label += ' · Suit';
      else if (norm.caseType === 'Guardianship / Custody') label += ' · Custody';
      else if (norm.caseType === 'Family Execution') label += ' · Execution';
      else if (norm.caseType === 'Family Appeal') label += ' · Appeal';
      else if (norm.caseType === 'Family Miscellaneous Application') label += ' · Application';
      else label += ` · ${norm.caseType}`;
      
      if (norm.caseSubtype && norm.caseSubtype !== 'Other') {
        label += ` · ${norm.caseSubtype.replace(/Execution of |Appeal Against |Application for |Interim |Application /g, '').trim()}`;
      } else if (norm.primaryRelief) {
        label += ` · ${norm.primaryRelief}`;
      }
    }
  } else if (norm.caseCategory === 'Constitutional / Writ') {
    label = 'Writ';
    if (norm.caseType) {
      if (norm.caseType === 'Writ Petition') label += ' · Petition';
      else if (norm.caseType === 'Service Matter') label += ' · Service';
      else if (norm.caseType === 'Habeas Corpus') label += ' · Habeas';
      else if (norm.caseType === 'Public Law / Fundamental Rights') label += ' · Fundamental Rights';
      else if (norm.caseType === 'Constitutional Petition') label += ' · Constitutional Petition';
      else if (norm.caseType === 'Judicial Review') label += ' · Judicial Review';
      else if (norm.caseType === 'Intra-Court Appeal') label += ' · ICA';
      else if (norm.caseType === 'Contempt Petition') label += ' · Contempt';
      else if (norm.caseType === 'Constitutional Miscellaneous Application') label += ' · CMA';
      else if (
        norm.caseType === 'Other Constitutional Matter' ||
        norm.caseType === 'Other Constitutional / Writ Matter'
      ) {
        label += ' · Other';
      } else label += ` · ${norm.caseType}`;
      
      if (norm.caseSubtype && norm.caseSubtype !== 'Other') {
        const subtypeLabels: Record<string, string> = {
          'Non-Compliance of Court Order': 'Non-Compliance',
          'ICA Against Writ Judgment': 'Against Writ Judgment',
        };
        label += ` · ${subtypeLabels[norm.caseSubtype] || norm.caseSubtype}`;
      } else if (norm.primaryRelief) {
        label += ` · ${norm.primaryRelief}`;
      }
    }
  } else if (norm.caseCategory === 'Rent / Tenancy') {
    label = 'Rent';
    if (norm.caseType) {
      if (norm.caseType === 'Eviction / Ejectment Petition') label += ' · Eviction';
      else if (norm.caseType === 'Rent Recovery') label += ' · Recovery';
      else if (norm.caseType === 'Rent Deposit / Consignment') label += ' · Deposit';
      else if (norm.caseType === 'Tenant Protection / Injunction') label += ' · Protection';
      else if (norm.caseType === 'Rent Appeal') label += ' · Appeal';
      else if (norm.caseType === 'Rent Revision / Constitutional Challenge') label += ' · Revision';
      else if (norm.caseType === 'Rent Miscellaneous Application') label += ' · Application';
      else label += ` · ${norm.caseType}`;
      
      if (norm.caseSubtype && norm.caseSubtype !== 'Other') {
        label += ` · ${norm.caseSubtype.replace(/Appeal Against |Application for |Protection Against |Deposit of |Deposit Due to /g, '').trim()}`;
      } else if (norm.primaryRelief) {
        label += ` · ${norm.primaryRelief}`;
      }
    }
  } else if (norm.caseCategory === 'Revenue / Land') {
    label = 'Revenue';
    if (norm.caseType) {
      if (norm.caseType === 'Mutation Matter') label += ' · Mutation';
      else if (norm.caseType === 'Partition / Taqseem') label += ' · Partition';
      else if (norm.caseType === 'Correction of Revenue Record') label += ' · Record Correction';
      else if (norm.caseType === 'Demarcation / Measurement') label += ' · Demarcation';
      else if (norm.caseType === 'Land Possession / Dispossession') label += ' · Possession';
      else if (norm.caseType === 'Revenue Appeal') label += ' · Appeal';
      else if (norm.caseType === 'Revenue Revision') label += ' · Revision';
      else if (norm.caseType === 'Board of Revenue Matter') label += ' · BOR';
      else if (norm.caseType === 'Land Acquisition / Compensation') label += ' · Acquisition';
      else if (norm.caseType === 'Revenue Miscellaneous Application') label += ' · Application';
      else label += ` · ${norm.caseType.replace(' Matter', '').replace(' Case', '')}`;
      
      if (norm.caseSubtype && norm.caseSubtype !== 'Other') {
        const subtypeShort = norm.caseSubtype
           .replace('Appeal Against ', '')
           .replace('Revision Against ', '')
           .replace('Application for ', '')
           .replace('Partition of ', '')
           .replace('Correction of ', '')
           .replace('Board of Revenue ', '');
        label += ` · ${subtypeShort.trim()}`;
      } else if (norm.primaryRelief) {
        label += ` · ${norm.primaryRelief}`;
      }
    }
  } else if (norm.caseCategory === 'Banking / Financial Recovery') {
    label = 'Banking';
    if (norm.caseType) {
      if (norm.caseType.includes('Suit')) label += ' · Suit';
      else if (norm.caseType.includes('Appeal')) label += ' · Appeal';
      else label += ` · ${norm.caseType.replace(' Matter', '')}`;
      if (norm.caseSubtype && norm.caseSubtype !== 'Other') label += ` · ${norm.caseSubtype}`;
    }
  } else if (norm.caseCategory === 'Consumer Protection') {
    label = 'Consumer';
    if (norm.caseType) {
      label += ` · ${norm.caseType.replace(' Protection', '')}`;
      if (norm.caseSubtype && norm.caseSubtype !== 'Other') label += ` · ${norm.caseSubtype}`;
    }
  } else if (norm.caseCategory === 'NAB / Accountability') {
    label = 'NAB';
    if (norm.caseType) {
      label += ` · ${norm.caseType.replace('NAB ', '').replace(' Matter', '')}`;
      if (norm.caseSubtype && norm.caseSubtype !== 'Other') label += ` · ${norm.caseSubtype}`;
    }
  } else if (norm.caseCategory === 'FIA / Cybercrime / Federal Offences') {
    label = 'FIA';
    if (norm.caseType) {
      label += ` · ${norm.caseType.replace('FIA ', '').replace(' Matter', '')}`;
      if (norm.caseSubtype && norm.caseSubtype !== 'Other') label += ` · ${norm.caseSubtype}`;
    }
  }
  return label;
};

export const getCaseClassificationBadges = (caseData: any) => {
  const norm = normalizeCivilCaseClassification(caseData);
  const badges = [];
  
  if (norm.caseCategory) {
    if (norm.caseCategory === 'Constitutional / Writ') {
      badges.push('Writ');
    } else if (norm.caseCategory === 'Revenue / Land') {
      badges.push('Revenue');
    } else if (norm.caseCategory === 'Banking / Financial Recovery') {
      badges.push('Banking');
    } else if (norm.caseCategory === 'Consumer Protection') {
      badges.push('Consumer');
    } else if (norm.caseCategory === 'NAB / Accountability') {
      badges.push('NAB');
    } else if (norm.caseCategory === 'FIA / Cybercrime / Federal Offences') {
      badges.push('FIA');
    } else if (norm.caseCategory === 'Rent / Tenancy') {
      badges.push('Rent');
    } else {
      badges.push(norm.caseCategory);
    }
  }
  
  if (norm.caseCategory === 'Civil' && norm.caseType) {
    if (norm.caseType.includes('Appeal')) badges.push('Appeal');
    else if (norm.caseType.includes('Revision')) badges.push('Revision');
    else if (norm.caseType.includes('Execution')) badges.push('Execution');
    else if (norm.caseType.includes('Application')) badges.push('Application');
    else if (norm.caseType.includes('Suit')) badges.push('Suit');
    else badges.push(norm.caseType);
    
    if (norm.caseSubtype && norm.caseSubtype !== 'Other') {
      badges.push(norm.caseSubtype);
    } else if (norm.primaryRelief) {
      badges.push(norm.primaryRelief);
    }
  } else if (norm.caseCategory === 'Consumer Protection' && norm.caseType) {
    if (norm.caseType === 'Consumer Complaint') badges.push('Complaint');
    else if (norm.caseType === 'Defective Goods Claim') badges.push('Goods');
    else if (norm.caseType === 'Deficiency in Service Claim') badges.push('Service');
    else if (norm.caseType === 'Medical Negligence / Professional Service Complaint') badges.push('Medical');
    else if (norm.caseType === 'Builder / Developer / Housing Society Complaint') badges.push('Builder');
    else if (norm.caseType === 'Utility / Telecom / Online Service Complaint') {
      if (norm.caseSubtype && norm.caseSubtype.includes('Online')) badges.push('Online');
      else if (norm.caseSubtype && norm.caseSubtype.includes('Telecom')) badges.push('Telecom');
      else badges.push('Utility');
    }
    else if (norm.caseType === 'Consumer Appeal') badges.push('Appeal');
    else if (norm.caseType === 'Consumer Execution / Compliance') badges.push('Compliance');
    else if (norm.caseType === 'Consumer Miscellaneous Application') badges.push('Application');
    else badges.push('Other');

    if (norm.caseSubtype && norm.caseSubtype !== 'Other') {
      let subtypeBadge = norm.caseSubtype;
      subtypeBadge = subtypeBadge.replace(/Appeal Against /g, '').replace(/Compliance of /g, '').replace(/Application for /g, '').trim();
      if (subtypeBadge.includes('General Consumer Complaint')) subtypeBadge = 'General';
      else if (subtypeBadge.includes('Wrong Billing / Overbilling')) subtypeBadge = 'Wrong Billing';
      else if (subtypeBadge.includes('Medical Negligence')) subtypeBadge = 'Negligence';
      else if (subtypeBadge.includes('Delay in Possession / Delivery')) subtypeBadge = 'Delay in Possession';
      else if (subtypeBadge.includes('Electricity Billing Complaint')) subtypeBadge = 'Electricity Billing';
      else if (subtypeBadge.includes('Online Purchase / E-Commerce Complaint')) subtypeBadge = 'E-Commerce';
      else if (subtypeBadge.includes('Appeal Against Dismissal of Complaint')) subtypeBadge = 'Dismissal Order';
      else if (subtypeBadge.includes('Compliance of Refund Order')) subtypeBadge = 'Refund Order';
      else if (subtypeBadge.includes('Application for Expert Inspection / Report')) subtypeBadge = 'Expert Inspection';
      badges.push(subtypeBadge);
    } else if (norm.primaryRelief) {
      badges.push(norm.primaryRelief);
    }
  } else if (norm.caseCategory === 'NAB / Accountability' && norm.caseType) {
    if (norm.caseType === 'NAB Inquiry / Call-Up Notice Matter') badges.push('Inquiry');
    else if (norm.caseType === 'NAB Investigation Matter') badges.push('Investigation');
    else if (norm.caseType === 'NAB Reference / Trial') badges.push('Reference');
    else if (norm.caseType === 'NAB Bail') badges.push('Bail');
    else if (norm.caseType === 'Remand / Custody Matter') badges.push('Remand');
    else if (norm.caseType === 'Voluntary Return / Plea Bargain') badges.push('VR/PB');
    else if (norm.caseType === 'Freezing / Attachment of Property') badges.push('Freezing');
    else if (norm.caseType === 'NAB Appeal') badges.push('Appeal');
    else if (norm.caseType === 'NAB Revision / Constitutional Challenge') badges.push('Writ');
    else if (norm.caseType === 'NAB Miscellaneous Application') badges.push('Application');
    else badges.push('Other');

    if (norm.caseSubtype && norm.caseSubtype !== 'Other') {
      let subtypeBadge = norm.caseSubtype;
      subtypeBadge = subtypeBadge.replace(/Appeal Against /g, '').replace(/Writ Against /g, '').replace(/Challenge to /g, '').replace(/Application for /g, '').replace(/ Freezing of /g, ' ').replace(/NAB /g, '').trim();
      badges.push(subtypeBadge);
    } else if (norm.primaryRelief) {
      badges.push(norm.primaryRelief);
    }
  } else if (norm.caseCategory === 'FIA / Cybercrime / Federal Offences' && norm.caseType) {
    if (norm.caseType === 'FIA Inquiry / Notice Matter') badges.push('Inquiry');
    else if (norm.caseType === 'FIA Investigation Matter') badges.push('Investigation');
    else if (norm.caseType === 'Cybercrime / PECA Matter') badges.push('Cybercrime');
    else if (norm.caseType === 'Electronic Fraud / Financial Cybercrime') badges.push('Fraud');
    else if (norm.caseType === 'Human Trafficking / Migrant Smuggling Matter') badges.push('Trafficking');
    else if (norm.caseType === 'Immigration / Passport / Exit Control Matter') badges.push('Immigration');
    else if (norm.caseType === 'Anti-Money Laundering Matter') badges.push('AML');
    else if (norm.caseType === 'FIA Challan / Trial') badges.push('Trial');
    else if (norm.caseType === 'FIA Bail') badges.push('Bail');
    else if (norm.caseType === 'Remand / Custody Matter') badges.push('Remand');
    else if (norm.caseType === 'FIA Appeal') badges.push('Appeal');
    else if (norm.caseType === 'FIA Revision / Constitutional Challenge') badges.push('Writ');
    else if (norm.caseType === 'FIA Miscellaneous Application') badges.push('Application');
    else badges.push('Other');

    if (norm.caseSubtype && norm.caseSubtype !== 'Other') {
      let subtypeBadge = norm.caseSubtype;
      subtypeBadge = subtypeBadge.replace(/Appeal Against /g, '').replace(/Appeal in /g, '').replace(/Writ Against /g, '').replace(/Challenge to /g, '').replace(/Application for /g, '').replace(/Bail in /g, '').replace(/FIA /g, '').replace(/ Case/g, '').trim();
      badges.push(subtypeBadge);
    } else if (norm.primaryRelief) {
      badges.push(norm.primaryRelief);
    }
  } else if (norm.caseCategory === 'Criminal') {
    const category = (norm as any).criminalCategory as string | undefined;
    const proceeding = ((norm as any).proceedingType || norm.caseType) as string | undefined;
    if (category) {
      badges.push(category.replace(' Matters', '').replace(' Criminal Jurisdiction', ' HC'));
    } else if (norm.caseType) {
      if (norm.caseType.includes('Appeal')) badges.push('Appeal');
      else if (norm.caseType.includes('Revision')) badges.push('Revision');
      else if (norm.caseType.includes('Application')) badges.push('Application');
      else if (norm.caseType.includes('Trial')) badges.push('Trial');
      else if (norm.caseType.includes('Bail')) badges.push('Bail');
      else if (norm.caseType.includes('Complaint')) badges.push('Complaint');
      else if (norm.caseType.includes('Quashment')) badges.push('Quashment');
      else badges.push(norm.caseType);
    }
    if (proceeding && proceeding !== category) {
      badges.push(proceeding);
    } else if (norm.caseSubtype && norm.caseSubtype !== 'Other') {
      badges.push(norm.caseSubtype);
    }
  } else if (norm.caseCategory === 'Family' && norm.caseType) {
    if (norm.caseType === 'Family Suit') badges.push('Suit');
    else if (norm.caseType === 'Guardianship / Custody') badges.push('Custody');
    else if (norm.caseType === 'Family Execution') badges.push('Execution');
    else if (norm.caseType === 'Family Appeal') badges.push('Appeal');
    else if (norm.caseType === 'Family Miscellaneous Application') badges.push('Application');
    else badges.push(norm.caseType);
    
    if (norm.caseSubtype && norm.caseSubtype !== 'Other') {
      badges.push(norm.caseSubtype.replace(/Execution of |Appeal Against |Application for |Interim |Application /g, '').trim());
    } else if (norm.primaryRelief) {
      badges.push(norm.primaryRelief);
    }
  } else if (norm.caseCategory === 'Constitutional / Writ' && norm.caseType) {
    if (norm.caseType === 'Writ Petition') badges.push('Petition');
    else if (norm.caseType === 'Service Matter') badges.push('Service');
    else if (norm.caseType === 'Habeas Corpus') badges.push('Habeas');
    else if (norm.caseType === 'Public Law / Fundamental Rights') badges.push('Fundamental Rights');
    else if (norm.caseType === 'Intra-Court Appeal') badges.push('ICA');
    else if (norm.caseType === 'Constitutional Miscellaneous Application') badges.push('CMA');
    else if (norm.caseType === 'Contempt Petition') badges.push('Contempt');
    else if (
      norm.caseType === 'Other Constitutional Matter' ||
      norm.caseType === 'Other Constitutional / Writ Matter'
    ) {
      badges.push('Other');
    } else badges.push(norm.caseType);
    
    if (norm.caseSubtype && norm.caseSubtype !== 'Other') {
      const subtypeLabels: Record<string, string> = {
        'Non-Compliance of Court Order': 'Non-Compliance',
        'ICA Against Writ Judgment': 'Against Writ Judgment',
      };
      badges.push(subtypeLabels[norm.caseSubtype] || norm.caseSubtype);
    } else if (norm.primaryRelief) {
      badges.push(norm.primaryRelief);
    }
  } else if (norm.caseCategory === 'Rent / Tenancy' && norm.caseType) {
    if (norm.caseType === 'Eviction / Ejectment Petition') badges.push('Eviction');
    else if (norm.caseType === 'Rent Recovery') badges.push('Recovery');
    else if (norm.caseType === 'Rent Deposit / Consignment') badges.push('Deposit');
    else if (norm.caseType === 'Tenant Protection / Injunction') badges.push('Protection');
    else if (norm.caseType === 'Rent Appeal') badges.push('Appeal');
    else if (norm.caseType === 'Rent Revision / Constitutional Challenge') badges.push('Revision');
    else if (norm.caseType === 'Rent Miscellaneous Application') badges.push('Application');
    else badges.push(norm.caseType);
    
    if (norm.caseSubtype && norm.caseSubtype !== 'Other') {
      badges.push(norm.caseSubtype.replace(/Appeal Against |Application for |Protection Against |Deposit of |Deposit Due to /g, '').trim());
    } else if (norm.primaryRelief) {
      badges.push(norm.primaryRelief);
    }
  } else if (norm.caseCategory === 'Revenue / Land' && norm.caseType) {
    if (norm.caseType === 'Mutation Matter') badges.push('Mutation');
    else if (norm.caseType === 'Partition / Taqseem') badges.push('Partition');
    else if (norm.caseType === 'Correction of Revenue Record') badges.push('Record Correction');
    else if (norm.caseType === 'Demarcation / Measurement') badges.push('Demarcation');
    else if (norm.caseType === 'Land Possession / Dispossession') badges.push('Possession');
    else if (norm.caseType === 'Revenue Appeal') badges.push('Appeal');
    else if (norm.caseType === 'Revenue Revision') badges.push('Revision');
    else if (norm.caseType === 'Board of Revenue Matter') badges.push('BOR');
    else if (norm.caseType === 'Land Acquisition / Compensation') badges.push('Acquisition');
    else if (norm.caseType === 'Revenue Miscellaneous Application') badges.push('Application');
    else badges.push(norm.caseType.replace(' Matter', '').replace(' Case', ''));
    
    if (norm.caseSubtype && norm.caseSubtype !== 'Other') {
      badges.push(
        norm.caseSubtype
           .replace('Appeal Against ', '')
           .replace('Revision Against ', '')
           .replace('Application for ', '')
           .replace('Partition of ', '')
           .replace('Correction of ', '')
           .replace('Board of Revenue ', '')
           .trim()
      );
    } else if (norm.primaryRelief) {
      badges.push(norm.primaryRelief);
    }
  } else if (norm.matterType || norm.caseType || norm.caseTypeName) {
      badges.push(norm.matterType || norm.caseType || norm.caseTypeName);
  }

  if (norm.additionalReliefs && norm.additionalReliefs.length > 0) {
    const rCount = norm.additionalReliefs.length;
    badges.push(`+${rCount} ${rCount === 1 ? 'relief' : 'reliefs'}`);
  }

  return badges;
};
